import { grupuriDescriere } from './descriere-premii';

/**
 * Optiunile de premiu ale unui nivel si alegerea profesorului la revendicare (item 15, decizia
 * owner-ului din 2026-09-21).
 *
 * Regula, fixa: **o linie din `descriere` = o optiune.** La revendicare alegerea e OBLIGATORIE si e
 * exact una:
 *
 *   - peste o optiune -> radio obligatoriu;
 *   - exact una       -> aleasa automat si afisata (nu ai ce alege, dar vezi ce primesti);
 *   - zero            -> textarea de dinainte, text liber.
 *
 * Alegerea pleaca in `alegereProfesor` si ajunge in emailul catre ECOTIC, in cel catre profesor si in
 * Istoric. Modul e PUR (vitest ruleaza pe `node`, fara jsdom): decizia sta aici, nu in `.tsx`, unde
 * n-o pazeste nicio poarta automata.
 */

/**
 * Liniile descrierii, APLATIZATE. Grupurile din `grupuriDescriere` (despartite de o linie goala) nu mai
 * inseamna nimic pentru alegere: fiecare linie e o optiune de sine statatoare.
 *
 * Duplicatele se scot (primul castiga): doua butoane radio cu acelasi text ar fi aceeasi alegere, iar
 * pe sarma ar pleca acelasi sir. Marcajele de lista si spatiile le scoate deja `grupuriDescriere`.
 */
export function optiuniPremiu(descriere: string | null | undefined): string[] {
    return [...new Set(grupuriDescriere(descriere).flat().filter((linie) => !DOAR_MARCAJ.test(linie)))];
}

/**
 * O linie care e DOAR un marcaj („-", „•"). `grupuriDescriere` o scapa: marcajul ei cere spatiu dupa,
 * iar linia e deja taiata, deci „-" ramane „-". Ca optiune ar fi un buton radio fara continut.
 */
const DOAR_MARCAJ = /^[-•*–—]$/;

export type ModAlegere =
    | { kind: 'text' }
    | { kind: 'automat'; optiune: string }
    | { kind: 'radio'; optiuni: string[] };

export function modAlegere(optiuni: string[]): ModAlegere {
    if (optiuni.length === 0) return { kind: 'text' };
    if (optiuni.length === 1) return { kind: 'automat', optiune: optiuni[0] };
    return { kind: 'radio', optiuni };
}

/** Textul erorii cand se trimite fara alegere. Unul singur, ca sa-l poata verifica si testul. */
export const EROARE_FARA_ALEGERE = 'Alege una dintre recompense ca să poți revendica premiul.';

export type RezultatAlegere =
    | { ok: true; alegereProfesor: string | undefined }
    | { ok: false; eroare: string };

/**
 * Ce pleaca in `alegereProfesor`, sau de ce nu pleaca nimic.
 *
 * `selectata` trebuie sa fie una dintre optiunile CURENTE: o valoare ramasa de la alt nivel (sheet
 * inchis, redeschis pe altul) nu are voie sa treaca drept alegere.
 *
 * La `text`, campul ramane optional, ca inainte: fara optiuni scrise de ECOTIC nu exista din ce alege,
 * iar textul liber e doar o nota. Un sir gol pleaca `undefined`, nu `""`.
 */
export function alegereDeTrimis(mod: ModAlegere, selectata: string | null, textLiber: string): RezultatAlegere {
    if (mod.kind === 'automat') return { ok: true, alegereProfesor: mod.optiune };
    if (mod.kind === 'radio') {
        return selectata !== null && mod.optiuni.includes(selectata)
            ? { ok: true, alegereProfesor: selectata }
            : { ok: false, eroare: EROARE_FARA_ALEGERE };
    }
    return { ok: true, alegereProfesor: textLiber.trim() || undefined };
}
